import {
  CheckCircleOutline,
  InfoOutlined,
  LocalOfferOutlined,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Divider,
  IconButton,
  Link,
  Popover,
  Stack,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";

import { amazonColors } from "../../../config/amazonTheme";
import { useExchangeRate } from "../../../hooks/useExchangeRate";
import { convertUsdToTjs } from "../../../services/exchangeRateService";
import { PaymentMethodDiscountValidationResult } from "../../../services/paymentMethodDiscountService";
import { TaxAndFeeSetting } from "../../../types/admin";
import {
  calculateServiceFee,
  calculateTax,
  roundCurrency,
} from "../../../utils/checkoutTotals";
import { formatApiError } from "../../../utils/formatApiError";
import Price from "../../common/Price";
import ShippingInfoCard from "../../common/ShippingInfoCard";

interface CheckoutSidebarProps {
  itemsCount: number;
  totalAmount: number;
  shippingCost: number;
  tax: number; // totalTaxAndFees
  taxAmount?: number;
  serviceFeeAmount?: number;
  finalTotal: number;
  taxAndFeeSettings?: TaxAndFeeSetting[];
  primaryActionLabel: React.ReactNode;
  primaryActionKey?: string;
  primaryActionDisabled?: boolean;
  isCalculatingShipping?: boolean;
  onPrimaryAction: () => void | Promise<void>;
  secondaryActionLabel?: string;
  onSecondaryAction?: () => void;
  snackbarMessage?: string;
  onSnackbarClose?: () => void;
  appliedPromoCode?: {
    code: string | null;
    codeId: string | null;
    discountAmount: number;
  };
  onPromoCodeChange?: (promoCode: {
    code: string | null;
    codeId: string | null;
    discountAmount: number;
  }) => void;
  paymentMethodDiscount?: PaymentMethodDiscountValidationResult | null;
  showActions?: boolean;
}

interface CheckoutSidebarActionsProps {
  primaryActionLabel: React.ReactNode;
  primaryActionKey?: string;
  primaryActionDisabled?: boolean;
  isCalculatingShipping?: boolean;
  onPrimaryAction: () => void | Promise<void>;
  secondaryActionLabel?: string;
  onSecondaryAction?: () => void;
  snackbarMessage?: string;
  onSnackbarClose?: () => void;
  topSpacing?: number;
}

export const CheckoutSidebarActions: React.FC<CheckoutSidebarActionsProps> = ({
  primaryActionLabel,
  primaryActionKey,
  primaryActionDisabled = false,
  isCalculatingShipping = false,
  onPrimaryAction,
  secondaryActionLabel,
  onSecondaryAction,
  snackbarMessage,
  onSnackbarClose,
  topSpacing,
}) => (
  <Box mt={topSpacing ?? 3}>
    {snackbarMessage && (
      <Alert severity="error" onClose={onSnackbarClose} sx={{ mb: 2 }}>
        {snackbarMessage}
      </Alert>
    )}

    <Button
      key={primaryActionKey || "primary-action"}
      variant="contained"
      onClick={onPrimaryAction}
      disabled={primaryActionDisabled || isCalculatingShipping}
      fullWidth
      size="large"
      sx={{
        bgcolor: amazonColors.orange,
        color: "white",
        fontWeight: 800,
        fontSize: "1rem",
        boxShadow: "0 4px 12px rgba(255, 153, 0, 0.3)",
        "&:hover": { bgcolor: amazonColors.orangeHover },
        "&:disabled": { bgcolor: "action.disabledBackground" },
      }}
    >
      {primaryActionLabel}
    </Button>

    {secondaryActionLabel && onSecondaryAction && (
      <Button
        variant="text"
        fullWidth
        onClick={onSecondaryAction}
        sx={{ mt: 1, color: "text.secondary", fontWeight: 600 }}
      >
        {secondaryActionLabel}
      </Button>
    )}
  </Box>
);

const CheckoutSidebar: React.FC<CheckoutSidebarProps> = ({
  itemsCount,
  totalAmount,
  shippingCost,
  tax,
  taxAmount,
  serviceFeeAmount,
  finalTotal: _finalTotal,
  taxAndFeeSettings = [],
  primaryActionLabel,
  primaryActionKey,
  primaryActionDisabled = false,
  isCalculatingShipping = false,
  onPrimaryAction,
  secondaryActionLabel,
  onSecondaryAction,
  snackbarMessage,
  onSnackbarClose,
  appliedPromoCode,
  onPromoCodeChange,
  paymentMethodDiscount,
  showActions = true,
}) => {
  const theme = useTheme();
  const { t: myTranslate } = useTranslation();

  const [infoAnchorEl, setInfoAnchorEl] = useState<HTMLButtonElement | null>(
    null
  );
  const [taxSavingsAnchorEl, setTaxSavingsAnchorEl] =
    useState<HTMLButtonElement | null>(null);
  const [promoSectionOpen, setPromoSectionOpen] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoStatus, setPromoStatus] = useState<{
    type: "error" | "success" | null;
    message: React.ReactNode | null;
  }>({ type: null, message: null });
  const [appliedDiscount, setAppliedDiscount] = useState(
    appliedPromoCode?.discountAmount ?? 0
  );
  const [appliedCode, setAppliedCode] = useState<string | null>(
    appliedPromoCode?.code ?? null
  );
  const [, setAppliedCodeId] = useState<string | null>(
    appliedPromoCode?.codeId ?? null
  );

  const { data: exchangeRate } = useExchangeRate();

  useEffect(() => {
    if (!appliedPromoCode) return;
    setAppliedDiscount(appliedPromoCode.discountAmount ?? 0);
    setAppliedCode(appliedPromoCode.code ?? null);
    setAppliedCodeId(appliedPromoCode.codeId ?? null);
  }, [
    appliedPromoCode?.code,
    appliedPromoCode?.codeId,
    appliedPromoCode?.discountAmount,
  ]);

  const actualTax =
    taxAmount ??
    (taxAndFeeSettings.length > 0
      ? calculateTax(totalAmount, taxAndFeeSettings)
      : 0);
  const serviceFee =
    serviceFeeAmount ??
    (taxAndFeeSettings.length > 0
      ? calculateServiceFee(totalAmount, taxAndFeeSettings)
      : 0);
  const totalTaxAndFees =
    taxAmount !== undefined ||
    serviceFeeAmount !== undefined ||
    taxAndFeeSettings.length > 0
      ? roundCurrency(actualTax + serviceFee)
      : tax;
  const taxSavingsRate = 0.0887;
  const estimatedTaxSavings = roundCurrency(totalAmount * taxSavingsRate);
  const typicalStoreTotal = roundCurrency(totalAmount + estimatedTaxSavings);

  const paymentMethodDiscountAmount =
    paymentMethodDiscount?.discountAmount ?? 0;
  const basePayToday = roundCurrency(totalAmount + totalTaxAndFees);
  const payToday = roundCurrency(
    Math.max(0, basePayToday - appliedDiscount - paymentMethodDiscountAmount)
  );
  const getPromoErrorMessage = (error: unknown) => {
    return formatApiError(error);
  };

  const handleApplyPromo = async () => {
    if (!promoCode.trim()) return;
    setPromoLoading(true);
    setPromoStatus({ type: null, message: null });

    try {
      const { validatePromotionCode } = await import(
        "../../../services/promotionCodeService"
      );
      const response = await validatePromotionCode({
        code: promoCode.trim().toUpperCase(),
        orderValue: totalAmount,
        fees: serviceFee,
        cargoFee: shippingCost || 0,
        route: "US2TJK",
      });

      if (!response.isSuccess || !response.data?.isValid) {
        throw new Error(
          response.data?.errorMessage ||
            response.message ||
            "PROMOTION_CODE_NOT_FOUND"
        );
      }

      const result = response.data;
      const discountValue = Number(result.discountAmount);
      const codeString = result.code?.code ?? promoCode.trim().toUpperCase();
      const codeId = result.code?.id ?? null;

      if (!discountValue)
        throw new Error("PROMOTION_CODE_INVALID_DISCOUNT");

      setAppliedDiscount(discountValue);
      setAppliedCode(codeString);
      setAppliedCodeId(codeId);
      setPromoStatus({
        type: "success",
        message: (
          <>
            Saved <Price value={discountValue} disableCentsHeuristic />!
          </>
        ),
      });

      if (onPromoCodeChange) {
        onPromoCodeChange({
          code: codeString,
          codeId,
          discountAmount: discountValue,
        });
      }

      setPromoSectionOpen(false);
      setPromoCode("");
    } catch (error: any) {
      setAppliedDiscount(0);
      setAppliedCode(null);
      setAppliedCodeId(null);
      setPromoStatus({
        type: "error",
        message: getPromoErrorMessage(error),
      });
      if (onPromoCodeChange) {
        onPromoCodeChange({ code: null, codeId: null, discountAmount: 0 });
      }
    } finally {
      setPromoLoading(false);
    }
  };

  const removePromo = () => {
    setAppliedDiscount(0);
    setAppliedCode(null);
    setAppliedCodeId(null);
    setPromoStatus({ type: null, message: null });
    if (onPromoCodeChange) {
      onPromoCodeChange({ code: null, codeId: null, discountAmount: 0 });
    }
  };

  return (
    <>
      <Card
        elevation={0}
        className="notranslate"
        translate="no"
        sx={{
          borderRadius: 3,
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: "0px 4px 20px rgba(0,0,0,0.04)",
          overflow: "visible",
        }}
      >
        <CardContent sx={{ p: 2.5 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography variant="h6" fontWeight={800}>
              {myTranslate("checkout.orderSummary")}
            </Typography>
            <Chip
              label={myTranslate("checkout.secureCheckout")}
              size="small"
              icon={<CheckCircleOutline sx={{ fontSize: 14 }} />}
              sx={{
                bgcolor: alpha(amazonColors.green, 0.1),
                color: amazonColors.green,
                fontWeight: 600,
                fontSize: "0.7rem",
                height: 24,
              }}
            />
          </Stack>

          <Stack spacing={1.5} mb={3}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="body2" color="text.secondary">
                {myTranslate("checkout.items")} ({itemsCount})
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                <Price value={totalAmount} disableCentsHeuristic tjsAmount={convertUsdToTjs(totalAmount, exchangeRate ?? null)} />
              </Typography>
            </Stack>

            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Typography variant="body2" color="text.secondary">
                  {myTranslate("checkout.taxesAndFees")}
                </Typography>
                <Tooltip title={myTranslate("checkout.salesTaxAndServiceFee")}>
                  <IconButton
                    size="small"
                    onClick={(e) => setInfoAnchorEl(e.currentTarget)}
                    sx={{ p: 0, color: "text.disabled" }}
                  >
                    <InfoOutlined fontSize="small" sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </Stack>
              <Typography variant="body2" fontWeight={600}>
                <Price value={totalTaxAndFees} disableCentsHeuristic tjsAmount={convertUsdToTjs(totalTaxAndFees, exchangeRate ?? null)} />
              </Typography>
            </Stack>

            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Typography
                  variant="body2"
                  color="success.main"
                  fontWeight={600}
                >
                  {myTranslate("checkout.youSaveOnTaxes")}
                </Typography>
                <IconButton
                  size="small"
                  onClick={(e) => setTaxSavingsAnchorEl(e.currentTarget)}
                  sx={{ p: 0, color: "success.light" }}
                >
                  <InfoOutlined fontSize="small" sx={{ fontSize: 16 }} />
                </IconButton>
              </Stack>
              <Typography
                variant="body2"
                color="success.main"
                fontWeight={700}
                sx={{ textDecoration: "line-through", opacity: 0.7 }}
              >
                <Price value={estimatedTaxSavings} disableCentsHeuristic tjsAmount={convertUsdToTjs(estimatedTaxSavings, exchangeRate ?? null)} />
              </Typography>
            </Stack>

            {(paymentMethodDiscountAmount > 0 || appliedDiscount > 0) && (
              <Divider sx={{ borderStyle: "dashed" }} />
            )}

            {paymentMethodDiscountAmount > 0 && (
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="body2" color="primary.main">
                  {myTranslate("checkout.paymentMethodDiscount")}
                </Typography>
                <Typography
                  variant="body2"
                  color="primary.main"
                  fontWeight={700}
                >
                  -
                  <Price
                    value={paymentMethodDiscountAmount}
                    disableCentsHeuristic
                    tjsAmount={convertUsdToTjs(paymentMethodDiscountAmount, exchangeRate ?? null)}
                  />
                </Typography>
              </Stack>
            )}

            {appliedCode && (
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Chip
                    label={appliedCode}
                    onDelete={removePromo}
                    size="small"
                    color="success"
                    variant="outlined"
                    sx={{ height: 20, fontSize: "0.7rem", fontWeight: 700 }}
                  />
                </Stack>
                <Typography
                  variant="body2"
                  color="success.main"
                  fontWeight={700}
                >
                  -<Price value={appliedDiscount} disableCentsHeuristic tjsAmount={convertUsdToTjs(appliedDiscount, exchangeRate ?? null)} />
                </Typography>
              </Stack>
            )}
          </Stack>

          <Box mb={2}>
            {!promoSectionOpen && !appliedCode ? (
              <Button
                variant="text"
                startIcon={<LocalOfferOutlined sx={{ fontSize: 18 }} />}
                onClick={() => setPromoSectionOpen(true)}
                size="small"
                sx={{
                  textTransform: "none",
                  color: amazonColors.blue,
                  fontWeight: 600,
                  p: 0,
                  "&:hover": {
                    bgcolor: "transparent",
                    textDecoration: "underline",
                  },
                }}
              >
                {myTranslate("checkout.havePromoCode")}
              </Button>
            ) : !appliedCode ? (
              <Collapse in={promoSectionOpen}>
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <TextField
                    size="small"
                    placeholder={myTranslate("checkout.enterCode")}
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                    fullWidth
                    error={promoStatus.type === "error"}
                    helperText={
                      promoStatus.type === "error" ? promoStatus.message : null
                    }
                    InputProps={{
                      style: { fontSize: "0.9rem" },
                    }}
                  />
                  <Button
                    variant="contained"
                    onClick={handleApplyPromo}
                    disabled={promoLoading || !promoCode.trim()}
                    sx={{
                      minWidth: 80,
                      bgcolor: "text.primary",
                      color: "white",
                      fontWeight: 700,
                      "&:hover": { bgcolor: "black" },
                    }}
                  >
                    {promoLoading
                      ? myTranslate("checkout.applying")
                      : myTranslate("checkout.apply")}
                  </Button>
                </Stack>
                <Button
                  size="small"
                  color="inherit"
                  onClick={() => {
                    setPromoSectionOpen(false);
                    setPromoStatus({ type: null, message: null });
                  }}
                  sx={{
                    fontSize: "0.7rem",
                    mt: 0.5,
                    textTransform: "none",
                    color: "text.secondary",
                  }}
                >
                  {myTranslate("common.cancel")}
                </Button>
              </Collapse>
            ) : null}
          </Box>

          <Divider sx={{ mb: 2 }} />

          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-end"
            mb={0.5}
          >
            <Typography variant="h6" fontWeight={700}>
              {myTranslate("checkout.payToday")}
            </Typography>
            <Stack alignItems="flex-end">
              <Typography
                variant="h5"
                fontWeight={800}
                color="text.primary"
                sx={{ lineHeight: 1 }}
              >
                <Price value={payToday} disableCentsHeuristic tjsAmount={convertUsdToTjs(payToday, exchangeRate ?? null)} />
              </Typography>
            </Stack>
          </Stack>

          {showActions && (
            <CheckoutSidebarActions
              primaryActionLabel={primaryActionLabel}
              primaryActionKey={primaryActionKey}
              primaryActionDisabled={primaryActionDisabled}
              isCalculatingShipping={isCalculatingShipping}
              onPrimaryAction={onPrimaryAction}
              secondaryActionLabel={secondaryActionLabel}
              onSecondaryAction={onSecondaryAction}
              snackbarMessage={snackbarMessage}
              onSnackbarClose={onSnackbarClose}
            />
          )}
          <Box sx={{ mt: 2 }}>
            <ShippingInfoCard
              shippingCost={shippingCost}
              isCalculating={isCalculatingShipping}
              applyMinimumFee
            />
          </Box>
        </CardContent>
      </Card>

      <Popover
        open={Boolean(infoAnchorEl)}
        anchorEl={infoAnchorEl}
        onClose={() => setInfoAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        PaperProps={{
          sx: { p: 2, borderRadius: 2 },
          className: "notranslate",
          translate: "no",
        }}
      >
        <Stack spacing={1} minWidth={180}>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="caption">{myTranslate("checkout.tax")}</Typography>
            <Typography variant="caption" fontWeight={600}>
              <Price value={actualTax} disableCentsHeuristic tjsAmount={convertUsdToTjs(actualTax, exchangeRate ?? null)} />
            </Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="caption">{myTranslate("checkout.serviceFee")}</Typography>
            <Typography variant="caption" fontWeight={600}>
              <Price value={serviceFee} disableCentsHeuristic tjsAmount={convertUsdToTjs(serviceFee, exchangeRate ?? null)} />
            </Typography>
          </Stack>
          <Divider />
          <Stack spacing={0.5}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: "0.65rem" }}
            >
              {myTranslate("checkout.serviceFeeExplanation")}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: "0.65rem", lineHeight: 1.4 }}
            >
              <strong>{myTranslate("checkout.serviceFeeRange")}</strong>
              <br />
              <strong>{myTranslate("checkout.serviceFeeDecreases")}</strong>
              <br />
              <strong>{myTranslate("checkout.noHiddenFees")}</strong>
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: "0.65rem" }}
            >
              <Link
                component={RouterLink}
                to="/terms#service-fee"
                underline="always"
                onClick={() => setInfoAnchorEl(null)}
              >
                {myTranslate("checkout.clickForMoreDetails")}
              </Link>
              .
            </Typography>
          </Stack>
        </Stack>
      </Popover>

      <Popover
        open={Boolean(taxSavingsAnchorEl)}
        anchorEl={taxSavingsAnchorEl}
        onClose={() => setTaxSavingsAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: { p: 0, width: 260, borderRadius: 3, overflow: "hidden" },
          className: "notranslate",
          translate: "no",
        }}
      >
        <Box
          sx={{
            bgcolor: "success.light",
            p: 2,
            color: "white",
            textAlign: "center",
          }}
        >
          <Typography variant="overline" fontWeight={700} sx={{ opacity: 0.9 }}>
            {myTranslate("checkout.estimatedSavings")}
          </Typography>
          <Typography variant="h4" fontWeight={800}>
            <Price value={estimatedTaxSavings} disableCentsHeuristic tjsAmount={convertUsdToTjs(estimatedTaxSavings, exchangeRate ?? null)} />
          </Typography>
        </Box>
        <Box p={2}>
          <Stack spacing={1}>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="caption" color="text.secondary">
                {myTranslate("checkout.newYorkTax")}
              </Typography>
              <Typography
                variant="caption"
                sx={{ textDecoration: "line-through" }}
              >
                <Price value={typicalStoreTotal} disableCentsHeuristic tjsAmount={convertUsdToTjs(typicalStoreTotal, exchangeRate ?? null)} />
              </Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography
                variant="subtitle2"
                fontWeight={700}
                color="success.main"
              >
                {myTranslate("checkout.us2tjkPrice")}
              </Typography>
              <Typography variant="subtitle2" fontWeight={700}>
                <Price value={totalAmount} disableCentsHeuristic tjsAmount={convertUsdToTjs(totalAmount, exchangeRate ?? null)} />
              </Typography>
            </Stack>
          </Stack>
        </Box>
      </Popover>
    </>
  );
};

export default CheckoutSidebar;
